import "katex/dist/katex.min.css"; // rehype-katex does not import the CSS
import ReactMarkdown from "react-markdown";
import { PrismAsyncLight as SyntaxHighlighter } from "react-syntax-highlighter";
import jsx from "react-syntax-highlighter/dist/esm/languages/prism/jsx";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import tsx from "react-syntax-highlighter/dist/esm/languages/prism/tsx";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeExternalLinks from "rehype-external-links";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import classes from "./MarkdownRenderer.module.css";
import "photoswipe/dist/photoswipe.css";
import { Gallery, Item } from "react-photoswipe-gallery";
import { Divider, Link, Typography, useTheme } from "@mui/material";
import {
  oneDark,
  oneLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { CopyToClipboardButton } from "../common/CopyToClipboardButton";
import { Children, isValidElement, useEffect } from "react";
import {
  Accordion,
  AccordionContextProvider,
  AccordionDetails,
  AccordionSummary,
} from "../common/Accordion";

SyntaxHighlighter.registerLanguage("jsx", jsx);
SyntaxHighlighter.registerLanguage("tsx", tsx);
SyntaxHighlighter.registerLanguage("python", python);

type Props = {
  markdown: string;
  inline?: boolean;
};

/**
 * Custom sanitize schema extending the default to allowlist the HTML elements
 * and attributes this component deliberately renders:
 *   - <details> / <summary> for accordion support
 *   - <div data-type data-accordion-expanded> for accordion context wrappers
 *   - <img data-thumbnail> for the photoswipe gallery
 *   - <iframe src style allow allowfullscreen> for embedded content
 *   - KaTeX math elements (span/svg/path with aria-* and specific classes)
 *
 * Dangerous elements (<script>, <style>, event handlers) remain blocked via
 * the default schema — only the items explicitly listed below are added.
 */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    // Accordion
    "details",
    "summary",
    // Embedded content
    "iframe",
    // KaTeX output elements
    "math",
    "mrow",
    "mi",
    "mo",
    "mn",
    "msup",
    "msub",
    "mfrac",
    "msubsup",
    "munder",
    "mover",
    "munderover",
    "mtable",
    "mtr",
    "mtd",
    "mtext",
    "mspace",
    "annotation",
    "semantics",
  ],
  attributes: {
    ...defaultSchema.attributes,
    // Allow target and rel on <a> so rehypeExternalLinks attributes survive
    a: [...(defaultSchema.attributes?.a ?? []), "target", "rel"],
    // Allow data-* on <div> for accordion wrappers
    div: [
      ...(defaultSchema.attributes?.div ?? []),
      "dataType",
      "dataAccordionExpanded",
    ],
    // Allow data-* on <details> for accordion items
    details: ["dataAccordionId", "dataAccordionTitle", "open"],
    // Allow data-thumbnail on <img> for the gallery
    img: [
      ...(defaultSchema.attributes?.img ?? []),
      "dataThumbnail",
      "width",
      "height",
    ],
    // Allow essential iframe attributes
    iframe: ["src", "width", "height", "style", "allow", "allowFullScreen"],
    // Allow KaTeX span classes and aria attributes
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      "className",
      "style",
      "ariaHidden",
    ],
    // Allow svg elements produced by KaTeX
    svg: ["xmlns", "width", "height", "viewBox", "ariaHidden", "style"],
    path: ["d", "stroke", "fill", "strokeWidth"],
    // Allow math element attributes
    math: ["xmlns", "display"],
    annotation: ["encoding"],
    // Allow className globally (e.g. for syntax highlighting), but NOT style —
    // restricting style to specific tags prevents CSS-based UI redress attacks.
    "*": [...(defaultSchema.attributes?.["*"] ?? []), "className"],
  },
};

function generateUniqueId(prefix = "") {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `${prefix}${timestamp}-${randomStr}`;
}

function GalleryImage({
  src,
  srcSet,
  alt,
  thumbnail,
  width,
  height,
}: React.ImgHTMLAttributes<HTMLImageElement> & { thumbnail?: string }) {
  // preload image
  useEffect(() => {
    const t = setTimeout(() => {
      const img = new Image();
      img.src = src ?? "";
    }, 100);
    return () => clearTimeout(t);
  }, [src]);

  return (
    <Item
      original={src}
      originalSrcset={srcSet}
      thumbnail={thumbnail ?? src}
      width={width}
      height={height ?? width}
    >
      {({ ref, open }) => (
        <img
          ref={ref}
          alt={alt}
          className="cursor-pointer"
          aria-hidden="true"
          onClick={(e) => {
            e.preventDefault();
            open(e);
          }}
          src={thumbnail ?? src}
        />
      )}
    </Item>
  );
}

const HEADING_SIZE_MAP: Record<
  "h1" | "h2" | "h3" | "h4" | "h5" | "h6",
  "h3" | "h4" | "h5" | "h6"
> = {
  h1: "h4",
  h2: "h5",
  h3: "h6",
  h4: "h6",
  h5: "h6",
  h6: "h6",
};

const createHeadingComponent = (
  variant: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
) =>
  function Heading({
    children,
    ...props
  }: React.ComponentPropsWithoutRef<"h1">) {
    return (
      <Typography
        variant={HEADING_SIZE_MAP[variant]}
        component={variant}
        {...props}
      >
        {children}
      </Typography>
    );
  };

export function MarkdownRenderer({ markdown, inline }: Props) {
  const theme = useTheme();
  const syntaxTheme = theme.palette.mode === "dark" ? oneDark : oneLight;

  const preprocessMarkdown = (markdownText: string) => {
    // Replace \[ with $$ and \] with $$ to ensure compatibility
    const processedText = markdownText
      .replaceAll("\\(\\pi\\)", "\\[(\\pi)\\]")
      .replaceAll("\\[", "$$$$")
      .replaceAll("\\]", "$$$$")
      .replaceAll("\\(", "$$")
      .replaceAll("\\)", "$$");
    return processedText;
  };

  return (
    <Gallery withDownloadButton>
      <div
        className={["relative", inline && classes["markdown-inline"]]
          .filter(Boolean)
          .join(" ")}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[
            rehypeRaw,
            [
              rehypeExternalLinks,
              { rel: ["nofollow noopener"], target: "_blank" },
            ],
            [rehypeKatex, { output: "html" }],
            [rehypeSanitize, sanitizeSchema],
          ]}
          components={{
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            a({ ref, node, ...props }) {
              return <Link {...props} />;
            },
            hr() {
              return <Divider className="my-2" />;
            },
            h1: createHeadingComponent("h1"),
            h2: createHeadingComponent("h2"),
            h3: createHeadingComponent("h3"),
            h4: createHeadingComponent("h4"),
            h5: createHeadingComponent("h5"),
            h6: createHeadingComponent("h6"),
            div({ node, children, ...props }) {
              const { dataType, dataAccordionExpanded, ...restProperties } =
                node?.properties || {};

              if (dataType === "accordion") {
                const expanded = dataAccordionExpanded?.toString() ?? "";
                return (
                  <div>
                    <AccordionContextProvider expanded={expanded}>
                      {children}
                    </AccordionContextProvider>
                  </div>
                );
              }

              return (
                <div {...restProperties} {...props}>
                  {children}
                </div>
              );
            },
            details({ node, children: _children }) {
              const accordionId =
                node?.properties?.dataAccordionId?.toString() || "";
              const accordionTitle =
                node?.properties?.dataAccordionTitle?.toString() || "";
              const open =
                node?.properties?.open === true ||
                node?.properties?.open === "true";

              let summaryContent: React.ReactNode = null;
              const contentChildren: React.ReactNode[] = [];

              Children.forEach(_children, (child) => {
                if (isValidElement(child) && child.type === "summary") {
                  summaryContent = child.props?.children;
                } else {
                  contentChildren.push(child);
                }
              });

              const displayTitle =
                accordionTitle || summaryContent || "Details";
              const id = accordionId || generateUniqueId();

              return (
                <Accordion id={id} expanded={open}>
                  <AccordionSummary id={id}>
                    {typeof displayTitle === "string" ? (
                      <Typography>{displayTitle}</Typography>
                    ) : (
                      displayTitle
                    )}
                  </AccordionSummary>
                  <AccordionDetails>{contentChildren}</AccordionDetails>
                </Accordion>
              );
            },
            img({ src, srcSet, alt, width, height, node }) {
              const thumbnail = node?.properties?.dataThumbnail;
              if (typeof thumbnail === "undefined") {
                return <img src={src} alt={alt} className="w-full" />;
              }
              return (
                <GalleryImage
                  src={src}
                  srcSet={srcSet}
                  alt={alt}
                  thumbnail={thumbnail?.toString()}
                  width={width}
                  height={height}
                />
              );
            },
            code({ className, children, ...props }) {
              const hasLang = /language-(\w+)/.exec(className || "");
              const content = String(children);

              return hasLang ? (
                <div className="relative">
                  {!inline && (
                    <div className="absolute right-4 top-4">
                      <CopyToClipboardButton text={content} />
                    </div>
                  )}
                  <SyntaxHighlighter
                    style={syntaxTheme}
                    language={hasLang[1]}
                    PreTag="div"
                    className="codeStyle"
                    showLineNumbers={true}
                    wrapLines
                    useInlineStyles={true}
                  >
                    {content}
                  </SyntaxHighlighter>
                </div>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            },
            iframe({ node, style: _style, ...props }) {
              const style: React.CSSProperties = {
                borderWidth: 0,
                width: "100%",
                height: "560px",
                ..._style,
              };

              return <iframe {...node?.properties} {...props} style={style} />;
            },
          }}
        >
          {preprocessMarkdown(markdown)}
        </ReactMarkdown>
      </div>
    </Gallery>
  );
}
